import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  List,
  Message,
  Modal,
  Popup,
  Progress,
  Segment,
  Table,
} from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { serverTimestamp } from "firebase/firestore";
import { closeModal, openModal } from "../../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import StrategyFeedback from "./StrategyFeedback";
import { buildPricingFeedbackConfig } from "./pricingFeedbackConfig";
import {
  functionGeneratePricingStrategy,
  functionResearchPropertyListing,
  functionFetchSubjectPropertyFromDataFiniti,
  functionFetchComparableSalesFromDataFiniti,
  functionGetStrategyEntitlement,
} from "../../../../app/firestore/functionsService";
import {
  updateTransactionStrategyInDb,
  updateTransactionSettingsInDb,
  addEmailToDb,
  sendNotificationEmail,
} from "../../../../app/firestore/firestoreService";
import { convertAddressFull } from "../../../../app/common/util/util";
import PricingStrategyResultBody from "./PricingStrategyResultBody";
import { formatSoldDate, parseDollarNumber, buildSiteSearchUrl, KNOWN_REAL_ESTATE_SITE_DOMAINS, buildDynamicDisclaimer, HOME_STATUS_OPTIONS } from "./pricingStrategyFormatting";
import { isCurrentCompSearchCache, isCurrentSubjectLookupCache } from "./strategyCacheVersions";
import ComparableReviewPanel from "./ComparableReviewPanel";
import { buildCompSelections, buildSelectedVerifiedComps, excludedIndicesFromSelections, isResultStale } from "./compSelection";

// result?.generatedAt is either a fresh client-side `new Date()` (just
// generated this session) or a Firestore Timestamp-shaped value read back
// from the saved generation doc (reopening an existing result) - which, once
// it crosses a Cloud Functions onCall response, is a plain object with no
// .toDate() method. Guard against both rather than letting date-fns' format()
// throw on an Invalid Date.
function toDisplayDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy 'at' h:mm a");
}

const conditionOptions = [
  { key: "excellent", text: "Excellent / move-in ready", value: "Excellent / move-in ready" },
  { key: "good", text: "Good, minor wear", value: "Good, minor wear" },
  { key: "fair", text: "Fair, some deferred maintenance", value: "Fair, some deferred maintenance" },
  { key: "needsWork", text: "Needs significant work", value: "Needs significant work" },
];

const timelineOptions = [
  { key: "asap", text: "ASAP / no flexibility", value: "ASAP / no flexibility" },
  { key: "1-3", text: "1-3 months", value: "1-3 months" },
  { key: "3-6", text: "3-6 months", value: "3-6 months" },
  { key: "6plus", text: "6+ months", value: "6+ months" },
  { key: "noRush", text: "No rush / flexible", value: "No rush / flexible" },
];

const financingOptions = [
  { key: "cash", text: "Cash", value: "Cash" },
  { key: "conventional", text: "Conventional loan", value: "Conventional loan" },
  { key: "fha", text: "FHA loan", value: "FHA loan" },
  { key: "va", text: "VA loan", value: "VA loan" },
  { key: "other", text: "Other / not sure yet", value: "Other / not sure yet" },
];

// overridePropertyDetails: the fields just entered in the "Unlock Intelligent
// Strategies?" modal (StrategyCreditConfirmModal), passed straight through
// via StrategyPricingModal's justEnteredPropertyDetails prop rather than
// read only from transaction.propertyDetails - that write just happened, and
// the Redux live listener updating the transaction prop here can lag behind
// this same-moment handoff, which would otherwise show these fields blank
// the instant this modal opens.
function buildDefaultInputs(transaction, overridePropertyDetails) {
  const isBuyer = transaction?.agentRepresents === "Buyer";
  const pd = { ...(transaction?.propertyDetails || {}), ...(overridePropertyDetails || {}) };
  return {
    address: transaction?.address ? convertAddressFull(transaction.address) : transaction?.title || "",
    currentPrice: transaction?.salesPrice ? `$${transaction.salesPrice}` : "",
    // Top-priority source categorizePropertyType() uses for comp-type
    // matching (functions/index.js), ahead of any MLS-imported subtype.
    propertyType: pd.propertyType || "",
    bedrooms: pd.bedroomsTotal ? String(pd.bedroomsTotal) : "",
    bathrooms: pd.bathroomsFull
      ? String(pd.bathroomsFull + (pd.bathroomsHalf || 0) * 0.5 + (pd.bathroomsThreeQuarter || 0) * 0.75)
      : "",
    squareFeet: pd.aboveGradeFinishedSqft ? String(pd.aboveGradeFinishedSqft) : "",
    lotSize: "",
    yearBuilt: pd.yearBuilt || "",
    timeOnMarket: "",
    taxAssessedValue: "",
    // Buyer-only (Offer Pricing Strategy) negotiation-leverage inputs - pre-
    // filled from verified public record (subjectLookup.publicRecord) when
    // found, otherwise left for the agent to fill in manually. See the
    // pre-fill effect below and buildPricingStrategyPrompt's handling of
    // subjectPublicRecord.originalListing/lastSale in functions/index.js.
    originalListDate: "",
    originalListPrice: "",
    sellerPurchasePrice: "",
    sellerPurchaseDate: "",
    // Agent-entered only, never auto-filled - see HOME_STATUS_OPTIONS in
    // pricingStrategyFormatting.js for why.
    homeStatus: "",
    keyFeatures: "",
    // condition is free text in the prompt, so it flows through fine even
    // though its wording ("New or Updated"/"Average"/"Dated"/"Needs Work")
    // doesn't match this form's own conditionOptions dropdown below - it
    // just won't show as pre-selected there if the user opens Edit Details.
    condition: pd.condition || "",
    updatesRenovations: "",
    knownIssues: "",
    locationPros: "",
    locationCons: "",
    comparableSales: "",
    competingInventory: "",
    timeline: "",
    budget: "",
    financingType: "",
    multipleOffers: "",
    additionalNotes: pd.additionalNotes || "",
    agentRepresents: isBuyer ? "Buyer" : "Seller",
  };
}

// The public-record provider sometimes lists the same recorded sale more
// than once - either as an exact (date, price) repeat (separate deed/parcel-
// index entries for one transaction), or, more often, as the same price with
// the date off by a few days (e.g. contract/close date vs. county recording
// date). Both showed up as near-identical entries repeated 2-3 times in
// "View full sale history." Treat entries with the same price and dates
// within SALE_DUPLICATE_WINDOW_DAYS of each other as one sale, keeping the
// first (most complete/most recent, per the source's own ordering) and
// dropping the rest.
const SALE_DUPLICATE_WINDOW_DAYS = 45;

function dedupeSaleHistory(saleHistory) {
  if (!Array.isArray(saleHistory)) return [];
  const windowMs = SALE_DUPLICATE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const kept = [];
  saleHistory.forEach((sale) => {
    const price = parseDollarNumber(sale?.price);
    const time = sale?.date ? new Date(sale.date).getTime() : NaN;
    const isDuplicate = kept.some((k) => {
      if (price === null || k.price === null || price !== k.price) return false;
      if (!Number.isFinite(time) || !Number.isFinite(k.time)) return sale?.date === k.sale?.date;
      return Math.abs(time - k.time) <= windowMs;
    });
    if (!isDuplicate) kept.push({ sale, price, time });
  });
  return kept.map((k) => k.sale);
}

// Real estate site names the research/comp prompts are allowed to report -
// buildSiteSearchUrl (imported from pricingStrategyFormatting) maps them to
// a domain for the "Research This Property" comp-search links below.
// Temporarily hides the manual "Verified Recent Solds" (Datafiniti) lookup
// section in favor of the web-search comps list with checkboxes. The
// automatic public-record comp search still runs during Generate Strategy
// regardless of this flag - it only controls the manual UI section.
const SHOW_DATAFINITI_COMPS_LOOKUP = false;

export default function StrategyPricingModal({ transaction, existingPricingResult, onGenerated, justEnteredPropertyDetails }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  // Previously generated result for the CURRENT property, reconstructed
  // server-side from transactions/{id}/strategyGenerations (spec sections
  // 29-31/63) - not transaction.strategy.pricing, a single slot that used to
  // get silently overwritten whenever a Buyer's address changed.
  const existingStrategy = existingPricingResult;
  const cachedPricingComps = transaction?.strategy?.pricingComps;
  const existingComps = isCurrentCompSearchCache(cachedPricingComps) ? cachedPricingComps : null;
  const existingResearch = transaction?.strategy?.propertyResearch;
  const existingSubjectLookup = transaction?.strategy?.subjectPropertyLookup;
  const isBuyer = transaction?.agentRepresents === "Buyer";

  const [step, setStep] = useState(() => {
    if (existingStrategy) return "result";
    if (existingResearch) return "rundown";
    return "researching";
  });
  const [inputs, setInputs] = useState(
    existingStrategy?.inputs || buildDefaultInputs(transaction, justEnteredPropertyDetails)
  );
  const [result, setResult] = useState(existingStrategy || null);
  const [error, setError] = useState(null);
  // Identifies one logical generation attempt to the server (functions/index.js's
  // generatePricingStrategy, via functions/strategyCredits/generationAttempts.js)
  // so a retry after a failure - the agent just clicking "Generate Strategy"
  // again, no dedicated Retry button needed - resumes/replays the SAME
  // request instead of silently starting a brand-new one. Reused only while
  // `error` is still set from the immediately-prior attempt; any other
  // click (first attempt, or "Edit Details & Regenerate" after a SUCCESS)
  // gets a fresh id since it's a genuinely new request.
  const pendingRequestIdRef = useRef(null);

  // True only when "form" was entered via "Edit Details & Regenerate" from
  // an existing result - as opposed to the first-time "Edit Details About
  // the Home" path from the rundown step. Drives two things: hiding
  // "Research This Property" (this property is already unlocked; re-
  // researching implies a different property, which costs another credit -
  // see below), and intercepting address edits with a confirmation instead
  // of quietly changing the property this Strategy is about.
  const [formEnteredFromResult, setFormEnteredFromResult] = useState(false);
  const [showDifferentPropertyPrompt, setShowDifferentPropertyPrompt] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newPropertyAddress, setNewPropertyAddress] = useState({ street: "", unit: "", city: "", state: "", zipcode: "" });
  const [switchingProperty, setSwitchingProperty] = useState(false);

  const [research, setResearch] = useState(existingResearch || null);
  const [researchError, setResearchError] = useState(null);

  // Fake-but-honest progress bar for the "researching"/"loading" steps -
  // there's no real percentage to report from a single AI call. Time-based
  // (not step-based) so the pacing is easy to reason about: it climbs
  // toward a 90% cap on a ~25s time constant, so a typical ~10-20s call
  // finishes well before it gets anywhere near the cap (often around
  // 30-55%) rather than racing up and then appearing to stall.
  const [loadingProgress, setLoadingProgress] = useState(0);
  useEffect(() => {
    if (step !== "loading" && step !== "researching") {
      setLoadingProgress(0);
      return;
    }
    const startedAt = Date.now();
    const CAP = 90;
    const TIME_CONSTANT_MS = 25000;
    setLoadingProgress(2);
    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setLoadingProgress(CAP * (1 - Math.exp(-elapsed / TIME_CONSTANT_MS)));
    }, 300);
    return () => clearInterval(interval);
  }, [step]);

  const [comps, setComps] = useState(existingComps?.comps || []);
  const [compSearchSummary, setCompSearchSummary] = useState(existingComps?.searchSummary || null);
  // Restored from the selections stored with the result, so reopening a
  // result doesn't misreport it as calculated from a different comp set.
  const [excludedCompIndices, setExcludedCompIndices] = useState(() =>
    excludedIndicesFromSelections(existingComps?.comps, existingStrategy?.inputs?.compSelections)
  );
  const [reviewCandidates, setReviewCandidates] = useState(existingComps?.reviewCandidates || []);
  const [reviewInclusions, setReviewInclusions] = useState(() => new Map(
    (existingStrategy?.inputs?.compSelections?.includedReviewCandidates || []).map((r) => [r.address, r.reason])
  ));
  // True once the agent changes the comp selection after a result exists.
  const [compSelectionDirty, setCompSelectionDirty] = useState(false);
  const [excludedWebCompIndices, setExcludedWebCompIndices] = useState(new Set());
  // Current competing inventory (homes actually listed for sale, not closed
  // sales) - kept fully separate from comps/excludedCompIndices above so
  // they never compete for the same "comp" slots and are never mistaken for
  // closed sales anywhere downstream.
  const [activeListings, setActiveListings] = useState(existingComps?.activeListings || []);
  const [excludedActiveListingIndices, setExcludedActiveListingIndices] = useState(new Set());
  const [compsLoading, setCompsLoading] = useState(false);
  const [compsError, setCompsError] = useState(null);
  const [compsFetchedAt] = useState(existingComps?.fetchedAt || null);

  const [subjectLookup, setSubjectLookup] = useState(existingSubjectLookup || null);

  // Buyer-only: pre-fill Original List Date/Price and Seller Purchase Price/
  // Date from the verified public record as soon as it's available (cached
  // or freshly fetched), but only into fields the agent hasn't already
  // filled in themselves - never overwrites a manual entry or a value
  // that's part of an already-saved regeneration's inputs.
  useEffect(() => {
    const pr = subjectLookup?.publicRecord;
    if (!isBuyer || !pr) return;
    setInputs((prev) => {
      const next = { ...prev };
      let changed = false;
      if (!prev.originalListPrice && Number.isFinite(pr.originalListing?.originalPrice)) {
        next.originalListPrice = `$${Number(pr.originalListing.originalPrice).toLocaleString()}`;
        changed = true;
      }
      if (!prev.originalListDate && pr.originalListing?.originalDateSeen) {
        next.originalListDate = formatSoldDate(pr.originalListing.originalDateSeen);
        changed = true;
      }
      if (!prev.sellerPurchasePrice && Number.isFinite(pr.lastSale?.amount)) {
        next.sellerPurchasePrice = `$${Number(pr.lastSale.amount).toLocaleString()}`;
        changed = true;
      }
      if (!prev.sellerPurchaseDate && pr.lastSale?.date) {
        next.sellerPurchaseDate = formatSoldDate(pr.lastSale.date);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [isBuyer, subjectLookup]);
  const [compsSearchAttempted, setCompsSearchAttempted] = useState(!!existingComps);
  const [generatingPhaseText, setGeneratingPhaseText] = useState("Generating your pricing strategy...");

  const [showClientExplanation, setShowClientExplanation] = useState(false);

  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState({ buttonLabel: "Give Feedback + Earn 1 Credit", alreadyDone: false });

  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [includeApproachesInEmail, setIncludeApproachesInEmail] = useState(false);
  const [includeCompsInEmail, setIncludeCompsInEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailBodyDraft, setEmailBodyDraft] = useState("");
  // Tracks whether the agent has hand-edited the draft, so toggling the
  // Include checkboxes only regenerates the suggested text when they
  // haven't - otherwise a checkbox toggle would silently overwrite edits.
  const [emailBodyEdited, setEmailBodyEdited] = useState(false);
  // Set when a checkbox is toggled after the agent has already started
  // hand-editing the draft - a checkbox toggle no longer touches their text
  // at that point, so this surfaces what to do about it instead of silently
  // doing nothing.
  const [checkboxNotice, setCheckboxNotice] = useState(null); // { label, checked, snippet } | null

  async function handleCopyClientExplanation() {
    try {
      await navigator.clipboard.writeText(result?.clientExplanation || "");
      toast.success("Copied to clipboard");
    } catch (err) {
      toast.error("Could not copy to clipboard");
    }
  }

  function getClientRecipients() {
    const clients = [
      transaction?.client,
      transaction?.clientSecondaryExists && transaction?.clientSecondary,
      transaction?.clientThirdExists && transaction?.clientThird,
    ].filter(Boolean);
    return clients
      .filter((c) => c?.email)
      .map((c) => ({
        email: c.email,
        firstName: c.firstName || "",
        name: `${c.firstName || ""} ${c.lastName || ""}`.trim(),
      }));
  }

  function buildClientGreeting() {
    const firstNames = getClientRecipients().map((r) => r.firstName).filter(Boolean);
    if (firstNames.length === 0) return "Hi,";
    if (firstNames.length === 1) return `Hi ${firstNames[0]},`;
    if (firstNames.length === 2) return `Hi ${firstNames[0]} and ${firstNames[1]},`;
    return `Hi ${firstNames.slice(0, -1).join(", ")}, and ${firstNames[firstNames.length - 1]},`;
  }

  // Extracted from buildClientEmailBody so the "you've already started
  // editing" notice can offer the exact same text as a standalone snippet
  // to copy-paste, rather than re-deriving it differently in two places.
  function buildApproachesSnippet() {
    if (!result?.pricingApproaches?.length) return "";
    return (
      "Pricing Approaches Considered:\n" +
      result.pricingApproaches
        .map((a) => `${a.name}${a.recommended ? " (Recommended)" : ""} - ${a.price}\n${a.explanation}`)
        .join("\n\n")
    );
  }

  function buildCompsSnippet() {
    if (!result?.comparableSales?.length) return "";
    return (
      "Comparable Sales:\n" +
      result.comparableSales
        .map((c) => {
          const details = [
            c.salePrice,
            c.saleDate && formatSoldDate(c.saleDate),
            c.squareFeet && `${c.squareFeet} sqft`,
          ].filter(Boolean).join(", ");
          return `${c.address || "Address not available"} - ${details}${c.relevance ? `\n${c.relevance}` : ""}`;
        })
        .join("\n\n")
    );
  }

  function buildClientEmailBody() {
    const sections = [buildClientGreeting(), result?.clientExplanation || ""];

    if (includeApproachesInEmail) sections.push(buildApproachesSnippet());
    if (includeCompsInEmail) sections.push(buildCompsSnippet());

    if (currentUserProfile?.emailSigField) {
      sections.push(currentUserProfile.emailSigField);
    }

    return sections.filter(Boolean).join("\n\n");
  }

  // Regenerates the suggested draft when the compose panel opens or the
  // Include checkboxes change - but only until the agent starts typing in
  // it, so their edits are never silently overwritten.
  useEffect(() => {
    if (showEmailCompose && !emailBodyEdited) {
      setEmailBodyDraft(buildClientEmailBody());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailCompose, includeApproachesInEmail, includeCompsInEmail, emailBodyEdited]);

  function handleResetEmailBody() {
    setEmailBodyDraft(buildClientEmailBody());
    setEmailBodyEdited(false);
    setCheckboxNotice(null);
  }

  async function handleCopySnippet(snippet) {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Copied to clipboard - paste it wherever you'd like in the email.");
    } catch (err) {
      toast.error("Could not copy to clipboard");
    }
  }

  // Once the agent has hand-edited the draft, toggling a checkbox no longer
  // touches their text (see the regeneration effect above) - this surfaces
  // that instead of the checkbox silently doing nothing.
  function handleToggleEmailSection(key, checked) {
    if (key === "approaches") setIncludeApproachesInEmail(checked);
    else setIncludeCompsInEmail(checked);

    if (emailBodyEdited) {
      setCheckboxNotice({
        label: key === "approaches" ? "Pricing Approaches" : "Comparable Sales",
        checked,
        snippet: key === "approaches" ? buildApproachesSnippet() : buildCompsSnippet(),
      });
    } else {
      setCheckboxNotice(null);
    }
  }

  async function handleSendClientEmail() {
    const recipients = getClientRecipients();
    if (recipients.length === 0) {
      toast.error("No email address is on file for the client(s) on this transaction.");
      return;
    }

    setSendingEmail(true);
    try {
      // The agent may have hand-edited the suggested draft - that edited
      // text is what actually gets sent, not the auto-generated version.
      const bodyText = (emailBodyDraft || "").trim() || buildClientEmailBody();
      const addressLine = transaction?.address ? convertAddressFull(transaction.address) : transaction?.title || "";
      const subject = `Pricing Strategy Update${addressLine ? ` - ${addressLine}` : ""}`;
      const toEmails = recipients.map((r) => r.email);
      const fromName = `${currentUserProfile?.firstName || ""} ${currentUserProfile?.lastName || ""}`.trim();

      // Agent's brokerage logo, if on file - appended to the actual sent
      // HTML and to what's saved in Email History, but intentionally never
      // rendered in this compose UI itself.
      const logoUrl = transaction?.agentProfile?.brokerageLogoRef || currentUserProfile?.brokerageLogoRef || null;
      const logoHtml = logoUrl
        ? `<p style="margin-top: 24px;"><img src="${logoUrl}" alt="Brokerage logo" style="max-height: 70px;" /></p>`
        : "";

      // bodyText is now free-form agent-edited text (previously always
      // machine-generated), so it must be HTML-escaped before going into the
      // HTML email/email-history view (which renders via
      // dangerouslySetInnerHTML) - otherwise stray "<"/">" characters could
      // break the markup or inject content.
      const escapedBodyHtml = bodyText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");

      const emailHistoryData = {
        transactionId: transaction.id,
        transactionTitle: transaction.title,
        from: currentUserProfile?.email,
        fromName,
        to: toEmails,
        toEmails,
        subject,
        message: bodyText,
        type: "pricing-strategy-email",
        sentAt: serverTimestamp(),
      };

      const mailOptions = {
        to: toEmails,
        from: `${fromName} <help@transactioner.com>`,
        replyTo: currentUserProfile?.email,
        message: {
          subject,
          text: bodyText,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
              <p style="white-space: pre-line;">${escapedBodyHtml}</p>
              <hr/>
              <p style="color: #666; font-size: 12px;">
                Sent from Transactioner by ${fromName}
              </p>
              ${logoHtml}
            </div>
          `,
        },
      };

      await sendNotificationEmail(mailOptions);
      await addEmailToDb(emailHistoryData, { message: mailOptions.message });

      toast.success("Email sent and added to Email History.");
      setShowEmailCompose(false);
      setEmailBodyEdited(false);
      setCheckboxNotice(null);
    } catch (err) {
      console.error("Failed to send pricing strategy email:", err);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  }

  function updateField(field, value) {
    setInputs((prev) => ({ ...prev, [field]: value }));
  }

  // Tracks whether each auto-researched field still holds what the web
  // search found (unverified) or was overwritten/typed by the agent, so the
  // AI prompt (and eventually the UI) can be honest about provenance rather
  // than presenting every fact as equally certain.
  function computeFieldSources() {
    if (!research) return {};
    const researchValues = {
      currentPrice: research.askingPrice,
      timeOnMarket: research.timeOnMarket,
      taxAssessedValue: research.taxAssessedValue,
      squareFeet: research.squareFeet,
      lotSize: research.lotSize,
      yearBuilt: research.yearBuilt,
      keyFeatures: (research.keyFeatures || []).join(", "),
    };
    const sources = {};
    Object.entries(researchValues).forEach(([field, researchValue]) => {
      if (!researchValue) return;
      if (inputs[field] === researchValue) {
        sources[field] = "Web Search";
      } else if (inputs[field]) {
        sources[field] = "Agent Provided";
      }
    });
    return sources;
  }

  async function handleResearch() {
    setResearchError(null);
    if (!inputs.address) {
      // Expected when the transaction has no address yet - not a real
      // failure, so skip the network call and don't log it as an error.
      setResearchError("This transaction needs an address before it can be researched.");
      setStep("rundown");
      return;
    }
    setStep("researching");
    try {
      const found = await functionResearchPropertyListing({
        address: inputs.address,
        isSeller: !isBuyer,
      });
      setResearch(found);
      setInputs((prev) => ({
        ...prev,
        currentPrice: prev.currentPrice || found.askingPrice || "",
        timeOnMarket: prev.timeOnMarket || found.timeOnMarket || "",
        taxAssessedValue: prev.taxAssessedValue || found.taxAssessedValue || "",
        squareFeet: prev.squareFeet || found.squareFeet || "",
        lotSize: prev.lotSize || found.lotSize || "",
        yearBuilt: prev.yearBuilt || found.yearBuilt || "",
        keyFeatures: prev.keyFeatures || (found.keyFeatures || []).join(", "),
      }));
      try {
        await updateTransactionStrategyInDb(transaction.id, "propertyResearch", {
          ...found,
          researchedAt: serverTimestamp(),
        });
      } catch (saveError) {
        // Non-fatal: the agent still sees the research even if caching it failed.
      }
    } catch (err) {
      console.error("researchPropertyListing failed:", err);
      setResearchError(
        err?.message || "Automatic research wasn't available for this property."
      );
    } finally {
      setStep("rundown");
    }
  }

  // Kick off research automatically the first time this modal opens for a
  // transaction with no cached research or strategy yet - the agent
  // shouldn't have to click anything just to see what's already knowable.
  useEffect(() => {
    if (step === "researching") {
      handleResearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleCompIncluded(idx) {
    setCompSelectionDirty(true);
    setExcludedCompIndices((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  // Never show a recommendation as current when it was calculated from a
  // different comp set than the one now selected (see compSelection.js).
  const resultIsStale = isResultStale({
    result, compList: comps, excludedIndices: excludedCompIndices,
    reviewList: reviewCandidates, reviewInclusions, selectionChanged: compSelectionDirty,
  });

  function includeReviewCandidate(address, reason) {
    setCompSelectionDirty(true);
    setReviewInclusions((prev) => new Map(prev).set(address, reason));
  }

  function removeReviewInclusion(address) {
    setCompSelectionDirty(true);
    setReviewInclusions((prev) => {
      const next = new Map(prev);
      next.delete(address);
      return next;
    });
  }

  function toggleActiveListingIncluded(idx) {
    setExcludedActiveListingIndices((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  function toggleWebCompIncluded(idx) {
    setExcludedWebCompIndices((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  // Looks up the subject's own Datafiniti record (property type, lat/long,
  // ownership, sale/financing history - see functions/index.js
  // lookupSubjectPropertyRecord) - cached on the transaction, with a TTL so
  // it isn't re-paid-for on every regenerate but also isn't stuck stale
  // forever (ownership/financing records do change over time). The backend
  // also caches this per-address across transactions, so even a "stale by
  // this transaction's clock" refetch is usually a free Firestore read, not
  // a new Datafiniti call.
  async function fetchSubjectLookupIfNeeded() {
    const rawFetchedAt = subjectLookup?.fetchedAt;
    const cachedAt = rawFetchedAt?.toDate ? rawFetchedAt.toDate() : (rawFetchedAt ? new Date(rawFetchedAt) : null);
    const cacheIsFresh = cachedAt && (Date.now() - cachedAt.getTime()) / (1000 * 60 * 60 * 24) <= 30;
    if (subjectLookup && cacheIsFresh && isCurrentSubjectLookupCache(subjectLookup)) return subjectLookup;

    const city = transaction?.address?.city;
    const state = transaction?.address?.state;
    if (!inputs.address || !city || !state) return null;

    try {
      const response = await functionFetchSubjectPropertyFromDataFiniti({
        address: inputs.address,
        city,
        state,
        postalCode: transaction?.address?.zipcode,
      });
      if (!response?.found) return null;
      const responseWithFetchedAt = { ...response, fetchedAt: new Date() };
      setSubjectLookup(responseWithFetchedAt);
      try {
        await updateTransactionStrategyInDb(transaction.id, "subjectPropertyLookup", {
          ...response,
          fetchedAt: serverTimestamp(),
        });
      } catch (saveError) {
        // Non-fatal: the agent still sees the result even if caching it failed.
      }
      return response;
    } catch (err) {
      console.error("fetchSubjectPropertyFromDataFiniti failed:", err);
      return null;
    }
  }

  async function handleFetchComps() {
    const city = transaction?.address?.city;
    const state = transaction?.address?.state;
    const postalCode = transaction?.address?.zipcode;
    if (!postalCode && !(city && state)) {
      setCompsError(
        "This transaction needs a city/state or zip code on file before comps can be looked up."
      );
      return;
    }

    setCompsError(null);
    setCompsLoading(true);
    try {
      const subject = await fetchSubjectLookupIfNeeded();
      const response = await functionFetchComparableSalesFromDataFiniti({
        address: inputs.address,
        city,
        state,
        postalCode,
        bedrooms: subject?.bedrooms ?? (parseInt(inputs.bedrooms, 10) || null),
        bathrooms: subject?.bathrooms ?? (parseFloat(inputs.bathrooms) || null),
        squareFeet: subject?.squareFeet ?? inputs.squareFeet,
        yearBuilt: subject?.yearBuilt ?? inputs.yearBuilt,
        lotSizeSqft: subject?.lotSizeSqft ?? null,
        propertyType: subject?.propertyType ?? null,
        // All subject type sources - the backend resolves them with the same
        // precedence generatePricingStrategy validates against.
        agentPropertyType: inputs.propertyType || null,
        mlsPropertySubType: transaction?.propertyDetails?.propertySubType || null,
        mlsPropertyAttached: transaction?.propertyDetails?.isPropertyAttached === true,
        latitude: subject?.latitude ?? null,
        longitude: subject?.longitude ?? null,
        // Lets the backend compare above-grade-to-above-grade when MLS data
        // is available, and resolve a price anchor for the competing-
        // listings search (functions/index.js's fetchComparableSalesFromDataFiniti) -
        // isSeller gates the budget fallback so a Buyer's budget is never
        // consulted for a Seller strategy.
        // MLS import first, then the MLS areas parsed from the subject's own
        // Datafiniti record - the same order generatePricingStrategy uses.
        aboveGradeFinishedSqft: transaction?.propertyDetails?.aboveGradeFinishedSqft ?? subject?.aboveGradeSqft ?? null,
        belowGradeFinishedSqft: transaction?.propertyDetails?.belowGradeFinishedSqft ?? subject?.belowGradeFinishedSqft ?? null,
        subjectSubdivision: subject?.subdivision || transaction?.propertyDetails?.subdivision || null,
        subjectBuildingName: subject?.buildingName || null,
        subjectNeighborhoods: subject?.neighborhoods || [],
        currentPrice: inputs.currentPrice || null,
        budget: inputs.budget || null,
        isSeller: !isBuyer,
      });
      const fetchedComps = response?.comps || [];
      const fetchedActiveListings = response?.activeListings || [];
      const searchSummary = response?.searchSummary || null;
      setComps(fetchedComps);
      setActiveListings(fetchedActiveListings);
      setCompSearchSummary(searchSummary);
      setReviewCandidates(response?.reviewCandidates || []);
      setReviewInclusions(new Map());
      setExcludedCompIndices(new Set());
      setExcludedActiveListingIndices(new Set());
      setCompsSearchAttempted(true);
      try {
        await updateTransactionStrategyInDb(transaction.id, "pricingComps", {
          comps: fetchedComps,
          activeListings: fetchedActiveListings,
          // Current pending sales (supporting evidence only, never sale prices).
          pendingListings: response?.pendingListings || [],
          reviewCandidates: response?.reviewCandidates || [],
          searchSummary,
          fetchedAt: serverTimestamp(),
          location: { city, state, postalCode },
        });
      } catch (saveError) {
        // Non-fatal: the agent still sees the comps even if caching them failed.
      }
      if (fetchedComps.length === 0 && fetchedActiveListings.length === 0) {
        toast.info("No comparable sales or active listings were found, even after expanding the search.");
      }
    } catch (err) {
      console.error("fetchComparableSalesFromDataFiniti failed:", err);
      setCompsError(
        err?.message || "Unable to look up comparable sales right now."
      );
      setCompsSearchAttempted(true);
    } finally {
      setCompsLoading(false);
    }
  }

  async function handleGenerate() {
    const previousStep = step;
    // Reuse the same requestId only when this click is a retry of the
    // immediately-prior FAILED attempt - any other click (the first attempt,
    // or a fresh "Edit Details & Regenerate" after a success) is a new
    // logical request and gets a new id.
    const isRetryOfFailedAttempt = Boolean(error) && Boolean(pendingRequestIdRef.current);
    if (!isRetryOfFailedAttempt) {
      pendingRequestIdRef.current = window.crypto.randomUUID();
    }
    setError(null);
    setGeneratingPhaseText("Looking up property records...");
    setStep("loading");
    let stillWorkingTimer = null;
    try {
      // Make a serious automatic attempt at real comps before generating -
      // the agent shouldn't have to click a separate button just because
      // the immediate neighborhood came up empty. Cached results (subject
      // lookup, prior comp search) are reused rather than re-paid-for.
      const subject = await fetchSubjectLookupIfNeeded();

      // React state updates from setComps/setCompSearchSummary below won't
      // be visible in this function's closure until the next render, so
      // track the freshly-fetched values locally and use those (not the
      // `comps` state variable) when building the payload further down.
      let currentComps = comps;
      let currentExcluded = excludedCompIndices;
      let currentActiveListings = activeListings;
      let currentExcludedActiveListings = excludedActiveListingIndices;
      let currentSearchSummary = compSearchSummary;
      let currentReviewCandidates = reviewCandidates;
      let currentReviewInclusions = reviewInclusions;

      if (!compsSearchAttempted) {
        setGeneratingPhaseText("Searching for comparable sales...");
        const city = transaction?.address?.city;
        const state = transaction?.address?.state;
        const postalCode = transaction?.address?.zipcode;
        if (postalCode || (city && state)) {
          try {
            const response = await functionFetchComparableSalesFromDataFiniti({
              address: inputs.address,
              city,
              state,
              postalCode,
              bedrooms: subject?.bedrooms ?? (parseInt(inputs.bedrooms, 10) || null),
              bathrooms: subject?.bathrooms ?? (parseFloat(inputs.bathrooms) || null),
              squareFeet: subject?.squareFeet ?? inputs.squareFeet,
              yearBuilt: subject?.yearBuilt ?? inputs.yearBuilt,
              lotSizeSqft: subject?.lotSizeSqft ?? null,
              propertyType: subject?.propertyType ?? null,
              agentPropertyType: inputs.propertyType || null,
              mlsPropertySubType: transaction?.propertyDetails?.propertySubType || null,
              mlsPropertyAttached: transaction?.propertyDetails?.isPropertyAttached === true,
              latitude: subject?.latitude ?? null,
              longitude: subject?.longitude ?? null,
              // MLS import first, then the MLS areas parsed from the subject's own
              // Datafiniti record - the same order generatePricingStrategy uses.
              aboveGradeFinishedSqft: transaction?.propertyDetails?.aboveGradeFinishedSqft ?? subject?.aboveGradeSqft ?? null,
              belowGradeFinishedSqft: transaction?.propertyDetails?.belowGradeFinishedSqft ?? subject?.belowGradeFinishedSqft ?? null,
              subjectSubdivision: subject?.subdivision || transaction?.propertyDetails?.subdivision || null,
              subjectBuildingName: subject?.buildingName || null,
              subjectNeighborhoods: subject?.neighborhoods || [],
              currentPrice: inputs.currentPrice || null,
              budget: inputs.budget || null,
              isSeller: !isBuyer,
            });
            currentComps = response?.comps || [];
            currentExcluded = new Set();
            currentActiveListings = response?.activeListings || [];
            currentExcludedActiveListings = new Set();
            currentSearchSummary = response?.searchSummary || null;
            currentReviewCandidates = response?.reviewCandidates || [];
            currentReviewInclusions = new Map();
            setReviewCandidates(currentReviewCandidates);
            setReviewInclusions(currentReviewInclusions);
            setComps(currentComps);
            setActiveListings(currentActiveListings);
            setCompSearchSummary(currentSearchSummary);
            setExcludedCompIndices(currentExcluded);
            setExcludedActiveListingIndices(currentExcludedActiveListings);
            try {
              await updateTransactionStrategyInDb(transaction.id, "pricingComps", {
                comps: currentComps,
                activeListings: currentActiveListings,
                pendingListings: response?.pendingListings || [],
                reviewCandidates: currentReviewCandidates,
                searchSummary: response?.searchSummary || null,
                fetchedAt: serverTimestamp(),
                location: { city, state, postalCode },
              });
            } catch (saveError) {
              // Non-fatal.
            }
          } catch (searchErr) {
            console.error("Automatic comp search failed:", searchErr);
          }
        }
        setCompsSearchAttempted(true);
      }

      setGeneratingPhaseText("Generating your pricing strategy...");
      // Neutral - deliberately makes no claim about a retry or provider
      // switch actually happening, since this is a single blocking request/
      // response call and the client has no real signal either way. Just
      // reassures the agent a long wait isn't necessarily a stall.
      stillWorkingTimer = setTimeout(
        () => setGeneratingPhaseText("Still working - complex properties can take a bit longer..."),
        20000
      );

      // User-facing source label is "Web Search" even though this data
      // actually comes from the public-record provider (Datafiniti) - the
      // vendor name means nothing to an agent. The internal sourcesUsed
      // tracking below still distinguishes it for the disclaimer logic.
      // isStructured (internal only, never shown) is how the backend tells
      // a real Datafiniti structured record apart from a genuine web-search
      // comp even though both display under the same honest "Web Search"
      // label - see compClassification.classifyComparable's sourceTier,
      // which only ever treats a structured, validated record as
      // "verified" (Strong-eligible).
      const datafinitiComps = buildSelectedVerifiedComps(currentComps, currentExcluded, currentReviewCandidates, currentReviewInclusions);
      // Stored with the result (inputs) as the audit trail of the agent's
      // comp decisions, including the reason for any exceptional inclusion.
      const compSelections = buildCompSelections(currentComps, currentExcluded, currentReviewCandidates, currentReviewInclusions);
      const inputsWithSelections = { ...inputs, compSelections };
      // Kept out of fetchedComps/datafinitiComps entirely (never a sold
      // comp) - sent as its own payload field so the prompt can only ever
      // use it for competitive-context framing, never as market-value
      // evidence. Uses the same honest "Web Search" label the agent sees
      // (the Datafiniti vendor name itself means nothing to them, matching
      // the sold-comp precedent above).
      const activeListingComps = currentActiveListings
        .filter((_, idx) => !currentExcludedActiveListings.has(idx))
        .map((c) => ({ ...c, source: "Web Search", isStructured: true }));
      const webComps = (research?.recentSolds || [])
        .filter((_, idx) => !excludedWebCompIndices.has(idx))
        .map((c) => ({
          address: c.address,
          soldPrice: c.soldPrice,
          soldDate: c.soldDate,
          notes: c.notes,
          source: "Web Search",
          sourceSite: c.sourceSite || null,
          isStructured: false,
          // Carried through when the research prompt found them, so the
          // backend's comp-quality gate (property type/size) can actually
          // apply to a web-found sale instead of defaulting to "unknown."
          squareFeet: c.squareFeet || null,
          propertyType: c.propertyType || null,
          bedrooms: c.bedrooms || null,
          bathrooms: c.bathrooms || null,
        }));
      const fieldSources = computeFieldSources();

      // Structural facts pulled from a live MLS import at deal creation, if
      // any - real MLS data, distinct from the auto-researched web fields.
      const pd = transaction?.propertyDetails || {};
      const mlsPropertyDetails = {
        bedroomsTotal: pd.bedroomsTotal ?? null,
        bathroomsFull: pd.bathroomsFull ?? null,
        bathroomsHalf: pd.bathroomsHalf ?? null,
        bathroomsThreeQuarter: pd.bathroomsThreeQuarter ?? null,
        aboveGradeFinishedSqft: pd.aboveGradeFinishedSqft ?? null,
        belowGradeFinishedSqft: pd.belowGradeFinishedSqft ?? null,
        belowGradeUnfinishedSqft: pd.belowGradeUnfinishedSqft ?? null,
        basement: pd.basement || [],
        garageSpaces: pd.garageSpaces ?? null,
        lotSizeSqft: pd.lotSizeSqft ?? null,
        levels: pd.levels || [],
        propertySubType: pd.propertySubType || "",
        // Only `true` is evidence - the MLS import defaults a missing
        // PropertyAttachedYN to false.
        propertyAttachedYN: pd.isPropertyAttached === true ? true : null,
        view: pd.view || [],
        waterfrontYN: pd.waterfrontYN ?? null,
        associationFee: pd.associationFee ?? null,
        associationFeeIncludes: pd.associationFeeIncludes || [],
        publicRemarks: pd.publicRemarks || "",
      };
      const hasMlsStructuralData = Object.values(mlsPropertyDetails).some((v) =>
        Array.isArray(v) ? v.length > 0 : v !== null && v !== ""
      );

      // Ground truth for the disclaimer, computed from what was actually
      // sent, not from anything the AI claims about itself.
      const sourcesUsed = [];
      if (inputs.comparableSales || Object.values(fieldSources).includes("Agent Provided")) {
        sourcesUsed.push("Agent Provided");
      }
      if (hasMlsStructuralData) sourcesUsed.push("MLS");
      if (datafinitiComps.length > 0 || activeListingComps.length > 0 || subject?.found) sourcesUsed.push("Datafiniti (Public Record)");
      if (webComps.length > 0 || Object.values(fieldSources).includes("Web Search")) sourcesUsed.push("Web Search");

      const generatedBy = currentUserProfile
        ? `${currentUserProfile.firstName || ""} ${currentUserProfile.lastName || ""}`.trim()
        : "";

      const generated = await functionGeneratePricingStrategy({
        transactionId: transaction.id,
        requestId: pendingRequestIdRef.current,
        // Round-tripped into the stored strategyGenerations doc as-is (see
        // functions/index.js's generatePricingStrategy), so reopening this
        // property's Pricing Strategy later reconstructs the exact same
        // display object without re-generating.
        clientInputs: inputsWithSelections,
        clientSourcesUsed: sourcesUsed,
        clientGeneratedBy: generatedBy,
        ...inputs,
        fetchedComps: [...datafinitiComps, ...webComps],
        // Current competing active listings (asking prices, not sold) - a
        // separate field so the prompt can never confuse these with actual
        // comparable sales; see functions/index.js's buildPricingStrategyPrompt.
        activeListingComps,
        fieldSources,
        mlsPropertyDetails,
        subjectPropertyType: subject?.propertyType || null,
        // Geography for the FHFA House Price Index cross-check - county is
        // the most reliable signal FHFA's metro-area data can be matched
        // against, with Datafiniti's verified lookup preferred over the
        // agent-entered/MLS-imported field.
        subjectCity: transaction?.address?.city || null,
        subjectState: transaction?.address?.state || null,
        subjectZip: transaction?.address?.zipcode || null,
        subjectCounty: subject?.county || transaction?.propertyDetails?.county || null,
        // Subject MLS areas parsed from its Datafiniti record - fallback for
        // buildSubjectProfile when the transaction has no MLS import.
        subjectAboveGradeSqft: subject?.aboveGradeSqft ?? null,
        subjectBelowGradeFinishedSqft: subject?.belowGradeFinishedSqft ?? null,
        subjectSaleHistory: subject?.saleHistory || [],
        // Full normalized ownership/sale/financing public record (see
        // lookupSubjectPropertyRecord) - the prompt only actually surfaces
        // latestRecordedFinancing today, but the whole object is passed
        // through so it's available for future use without another payload
        // change.
        subjectPublicRecord: subject?.publicRecord || null,
        // Only sent when there's no verified (Datafiniti/public-record) sale
        // history above - a lower-confidence web-research fallback, clearly
        // labeled as such in the prompt/output rather than treated as fact.
        subjectPriorSaleWebResearch:
          (!subject?.saleHistory || subject.saleHistory.length === 0) && research?.subjectPriorSale
            ? research.subjectPriorSale
            : null,
        compSearchSummary: currentSearchSummary || null,
      });
      clearTimeout(stillWorkingTimer);
      const strategyToSave = {
        ...generated,
        inputs: inputsWithSelections,
        sourcesUsed,
        generatedAt: new Date(),
        generatedBy,
      };
      // Consumed - a later click (e.g. a fresh "Edit Details & Regenerate")
      // is a new logical request and must get its own new id.
      pendingRequestIdRef.current = null;
      setResult(strategyToSave);
      setCompSelectionDirty(false);
      setStep("result");
      // Persisted server-side (transactions/{id}/strategyGenerations +
      // strategyProperties) inside generatePricingStrategy itself now,
      // keyed by property rather than overwriting a single
      // transaction.strategy.pricing slot - see spec sections 29-31/63.
      // Tell the dashboard right away too - otherwise its own
      // existingPricingResult/strategyProperty state stays stale until its
      // next fetch, so closing this modal and reopening it would start over
      // from scratch instead of showing what was just generated.
      if (onGenerated) {
        onGenerated({ generationId: generated.generationId, propertyKey: generated.propertyKey, result: strategyToSave });
      }
    } catch (err) {
      if (stillWorkingTimer) clearTimeout(stillWorkingTimer);
      console.error("generatePricingStrategy failed:", err);
      // Never surface the raw server/API error text - the credit was spent
      // (if at all) at the earlier unlock step, not by this attempt, so that
      // fact is always true to state regardless of what actually failed.
      const friendlyMessage =
        "We couldn't complete your strategy because the AI service is temporarily unavailable. Your property remains unlocked, and you can retry without using another credit.";
      setError(friendlyMessage);
      toast.error(friendlyMessage);
      setStep(previousStep === "rundown" ? "rundown" : "form");
    }
  }

  function handleClose() {
    dispatch(closeModal({ modalType: "StrategyPricingModal" }));
  }

  // Called instead of updateField("address", ...) while regenerating an
  // already-unlocked property - the address input stays frozen on its
  // original value and this prompt opens instead, so "start typing a
  // different address" can never silently change which property this
  // Strategy (and its already-spent credit) is about.
  function handleAddressFieldAttemptedChange() {
    setShowDifferentPropertyPrompt(true);
  }

  function handleCancelDifferentPropertyPrompt() {
    setShowDifferentPropertyPrompt(false);
  }

  function handleConfirmDifferentProperty() {
    setShowDifferentPropertyPrompt(false);
    setShowNewAddressForm(true);
  }

  function updateNewPropertyAddressField(field, value) {
    setNewPropertyAddress((prev) => ({ ...prev, [field]: value }));
  }

  function handleCancelNewAddressForm() {
    setShowNewAddressForm(false);
    setNewPropertyAddress({ street: "", unit: "", city: "", state: "", zipcode: "" });
  }

  // Saves the new address as the transaction's current property (the same
  // multi-property mechanism a Buyer transaction's address change already
  // uses - see functions/strategyCredits/propertyKey.js), fetches a fresh
  // entitlement for it, then hands off to the Unlock Intelligent Strategies
  // modal so the new property's credit is spent explicitly, not silently.
  async function handleSwitchToNewProperty() {
    if (!newPropertyAddress.street || !newPropertyAddress.city || !newPropertyAddress.state) return;
    setSwitchingProperty(true);
    try {
      // transaction.strategy.propertyResearch/subjectPropertyLookup/pricingComps
      // are single per-TRANSACTION slots, not per-property (unlike the
      // Strategy result itself, which lives in strategyGenerations/
      // strategyProperties keyed by property - see the comment atop
      // strategyStorage.js). Left alone, the new property's modal would
      // reuse the OLD property's cached recent solds, active listings, and
      // Datafiniti subject-property lookup - a real risk of showing comps
      // and "current listing" data for a home in a different neighborhood
      // or city entirely. Clear all three in the same write as the address
      // change so this property starts genuinely fresh.
      await updateTransactionSettingsInDb(transaction.id, {
        address: newPropertyAddress,
        "strategy.propertyResearch": null,
        "strategy.subjectPropertyLookup": null,
        "strategy.pricingComps": null,
      });
      const entitlementData = await functionGetStrategyEntitlement({ transactionId: transaction.id });
      dispatch(closeModal({ modalType: "StrategyPricingModal" }));
      dispatch(
        openModal({
          modalType: "StrategyCreditConfirmModal",
          modalProps: {
            transactionId: transaction.id,
            propertyAddress: newPropertyAddress,
            propertyPic: transaction.pic,
            triggerStrategyType: "pricing",
            entitlement: entitlementData,
            // Without this, confirming the unlock just closed the modal with
            // no follow-up - the new property WAS unlocked, but nothing ever
            // opened the Pricing modal back up for it, so it looked like
            // "nothing happened" and required manually reopening + hitting
            // Generate again. Mirrors TransactionStrategyDashboard.jsx's own
            // openPricingModal, built locally here since this switch
            // originates from inside the modal, not the dashboard.
            onConfirmed: (justEnteredPropertyDetails) => {
              dispatch(
                openModal({
                  modalType: "StrategyPricingModal",
                  modalProps: {
                    // Stripped of the OLD property's cached research/comps/
                    // subject-lookup (see above) so this fresh instance
                    // starts on "researching" for the NEW address instead
                    // of reading Redux's still-lagging transaction.strategy.*
                    // (the Firestore write above hasn't necessarily been
                    // reflected by the live listener yet).
                    transaction: {
                      ...transaction,
                      address: newPropertyAddress,
                      strategy: { ...transaction?.strategy, propertyResearch: null, subjectPropertyLookup: null, pricingComps: null },
                    },
                    existingPricingResult: null,
                    onGenerated,
                    justEnteredPropertyDetails,
                  },
                })
              );
            },
          },
        })
      );
    } catch (err) {
      toast.error(err?.message || "Unable to switch to the new property right now.");
      setSwitchingProperty(false);
    }
  }

  function renderLoadingStep({ explanation, note, loadingText }) {
    return (
      <>
        <Header color="blue" size="large">
          <Icon name="dollar" />
          {isBuyer ? "Offer Pricing Strategy" : "List Pricing Strategy"}
        </Header>
        <Segment secondary>
          <Header as="h4" style={{ marginBottom: 0 }}>
            <Icon name="home" />
            <Header.Content>
              {inputs.address || transaction?.title || "This property"}
            </Header.Content>
          </Header>
        </Segment>

        {explanation && <p>{explanation}</p>}
        {note && <p className="text-grey">{note}</p>}

        <div style={{ padding: "20px 0" }}>
          <Progress percent={Math.round(loadingProgress)} indicating size="small" />
          <p
            className="text-grey"
            style={{ fontStyle: "italic", marginTop: "6px", textAlign: "center" }}
          >
            {loadingText}
          </p>
        </div>
      </>
    );
  }

  function renderCompsLookup() {
    return (
      <Segment secondary>
        <Header as="h5" style={{ marginBottom: "4px" }}>
          <Icon name="search" />
          Verified Recent Solds &amp; Active Listings (optional)
        </Header>
        <p className="text-grey" style={{ fontSize: "0.9em" }}>
          The web search above is free but unverified. For up to 6 real,
          verified recent sold comps plus current homes listed for sale
          nearby, look them up from public records &mdash; this is a paid
          lookup, so it only runs when you click the button, and results are
          cached until you refresh them.
        </p>
        {compsError && <Message negative size="small" content={compsError} />}
        <Button
          type="button"
          size="small"
          icon
          labelPosition="left"
          loading={compsLoading}
          disabled={compsLoading}
          onClick={handleFetchComps}
        >
          <Icon name="search" />
          {comps.length > 0 ? "Refresh Comps" : "Look Up Comps"}
        </Button>
        {compsFetchedAt && comps.length > 0 && (
          <span className="text-grey" style={{ fontSize: "0.85em", marginLeft: "10px" }}>
            Showing comps from a previous lookup
          </span>
        )}

        {comps.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: "12px" }}>
            <Table celled compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell collapsing>Use</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Sold Price</Table.HeaderCell>
                  <Table.HeaderCell>Sold Date</Table.HeaderCell>
                  <Table.HeaderCell>Beds/Baths</Table.HeaderCell>
                  <Table.HeaderCell>Sqft</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {comps.map((comp, idx) => (
                  <Table.Row key={idx} disabled={excludedCompIndices.has(idx)}>
                    <Table.Cell>
                      <Checkbox
                        checked={!excludedCompIndices.has(idx)}
                        onChange={() => toggleCompIncluded(idx)}
                      />
                    </Table.Cell>
                    <Table.Cell>{comp.address || comp.city || "Unknown"}</Table.Cell>
                    <Table.Cell>
                      {typeof comp.soldPrice === "number"
                        ? `$${comp.soldPrice.toLocaleString()}`
                        : comp.soldPrice || "-"}
                    </Table.Cell>
                    <Table.Cell>{formatSoldDate(comp.soldDate)}</Table.Cell>
                    <Table.Cell>
                      {comp.bedrooms && comp.bathrooms ? `${comp.bedrooms}/${comp.bathrooms}` : "-"}
                    </Table.Cell>
                    <Table.Cell>{comp.squareFeet || "-"}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        {activeListings.length > 0 && (
          <>
            <p className="text-grey small top margin" style={{ fontSize: "0.9em" }}>
              Homes currently listed for sale nearby &mdash; asking prices, not closed sales. Used only
              as current-competition context, never as comparable sales evidence.
            </p>
            <div style={{ overflowX: "auto" }}>
              <Table celled compact size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell collapsing>Use</Table.HeaderCell>
                    <Table.HeaderCell>Address</Table.HeaderCell>
                    <Table.HeaderCell>Asking Price</Table.HeaderCell>
                    <Table.HeaderCell>Listed</Table.HeaderCell>
                    <Table.HeaderCell>Beds/Baths</Table.HeaderCell>
                    <Table.HeaderCell>Sqft</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {activeListings.map((listing, idx) => (
                    <Table.Row key={idx} disabled={excludedActiveListingIndices.has(idx)}>
                      <Table.Cell>
                        <Checkbox
                          checked={!excludedActiveListingIndices.has(idx)}
                          onChange={() => toggleActiveListingIncluded(idx)}
                        />
                      </Table.Cell>
                      <Table.Cell>{listing.address || listing.city || "Unknown"}</Table.Cell>
                      <Table.Cell>
                        {typeof listing.listPrice === "number"
                          ? `$${listing.listPrice.toLocaleString()}`
                          : listing.listPrice || "-"}
                      </Table.Cell>
                      <Table.Cell>{formatSoldDate(listing.listDate)}</Table.Cell>
                      <Table.Cell>
                        {listing.bedrooms && listing.bathrooms ? `${listing.bedrooms}/${listing.bathrooms}` : "-"}
                      </Table.Cell>
                      <Table.Cell>{listing.squareFeet || "-"}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
      </Segment>
    );
  }

  function renderRundownStep() {
    const rundownFields = [
      { label: isBuyer ? "Asking Price" : "List Price", value: inputs.currentPrice },
      // Property Type/Bedrooms/Square Feet/Condition/Notes: whatever the user
      // entered in the "Unlock Intelligent Strategies?" modal (or that was
      // already on file) shows up here first thing, before any research
      // happens - see buildDefaultInputs' justEnteredPropertyDetails merge.
      { label: "Property Type", value: inputs.propertyType },
      { label: "Bedrooms", value: inputs.bedrooms },
      { label: "Square Feet", value: inputs.squareFeet },
      { label: "Condition", value: inputs.condition },
      { label: "Time on Market", value: inputs.timeOnMarket },
      { label: "Tax Assessed Value", value: inputs.taxAssessedValue },
      { label: "Lot Size (sqft/acres)", value: inputs.lotSize },
      { label: "Key Features", value: inputs.keyFeatures },
      { label: "Notes", value: inputs.additionalNotes },
    ];

    return (
      <>
        <Header color="blue" size="large">
          <Icon name="dollar" />
          {isBuyer ? "Offer Pricing Strategy" : "List Pricing Strategy"}
        </Header>
        {inputs.address && (
          <p className="text-grey">
            Here's what the AI found searching the web for this property.
            Review it, then edit in anything else you know about the home
            before generating a strategy.
          </p>
        )}
        <Divider />
        {error && <Message negative content={error} />}
        {researchError && (
          <Message warning size="small">
            <Icon name="warning circle" />
            {researchError} The fields below are blank where nothing was
            found &mdash; fill them in with "Edit Details About the Home".
          </Message>
        )}

        {inputs.address && (
          <>
            <Header as="h5" color="blue">
              Current Listing Details
              <Popup
                trigger={
                  <Button
                    basic
                    compact
                    size="mini"
                    style={{ marginLeft: "10px", fontWeight: "normal" }}
                    onClick={handleResearch}
                  >
                    <Icon name="redo" size="small" />
                    Re-research
                  </Button>
                }
                content="Search the web again for this property's details, replacing what's shown below."
                position="top center"
                size="small"
              />
            </Header>
            <List relaxed divided>
              {rundownFields.map((field) => (
                <List.Item key={field.label}>
                  <List.Content>
                    <strong>{field.label}:</strong>{" "}
                    {field.value || <span className="text-grey">Not found</span>}
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </>
        )}

        {research?.recentSolds?.length > 0 && (
          <>
            <Header as="h5" color="blue">Recent Solds in the Neighborhood (via web search)</Header>
            <p className="text-grey" style={{ fontSize: "0.85em", marginTop: 0 }}>
              Uncheck any that shouldn't be used in the pricing analysis.
            </p>
            <List relaxed>
              {research.recentSolds.map((sold, idx) => (
                <List.Item key={idx}>
                  <Checkbox
                    checked={!excludedWebCompIndices.has(idx)}
                    onChange={() => toggleWebCompIncluded(idx)}
                    label={
                      <label>
                        <strong>{sold.address}</strong>
                        {sold.soldPrice && ` — ${sold.soldPrice}`}
                        {sold.soldDate && ` (${sold.soldDate})`}
                        {sold.notes && ` — ${sold.notes}`}
                      </label>
                    }
                  />
                </List.Item>
              ))}
            </List>
          </>
        )}

        {research?.sources?.length > 0 && (
          <p className="text-grey" style={{ fontSize: "0.85em" }}>
            Sources:{" "}
            {research.sources.map((sourceText, idx) => {
              const matchedSite = Object.keys(KNOWN_REAL_ESTATE_SITE_DOMAINS).find((site) =>
                sourceText.toLowerCase().includes(site.toLowerCase())
              );
              const url = matchedSite ? buildSiteSearchUrl(matchedSite, inputs.address) : null;
              return (
                <React.Fragment key={idx}>
                  {idx > 0 && ", "}
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">{sourceText}</a>
                  ) : (
                    sourceText
                  )}
                </React.Fragment>
              );
            })}
          </p>
        )}

        {/* The manual public-record comp lookup is hidden for now - the
            web-search comps above (with checkboxes) are the primary
            UI-visible comp source. The automatic public-record comp search
            still runs quietly during "Generate Strategy" either way. Flip
            SHOW_DATAFINITI_COMPS_LOOKUP to bring the manual section back. */}
        {SHOW_DATAFINITI_COMPS_LOOKUP && renderCompsLookup()}
      </>
    );
  }

  function renderFormStep() {
    return (
      <>
        <Header color="blue" size="large">
          <Icon name="dollar" />
          {isBuyer ? "Offer Pricing Strategy" : "List Pricing Strategy"}
        </Header>
        <p className="text-grey">
          Fill in what you know &mdash; leave anything blank that doesn't
          apply. The more detail you give, the better the recommendation.
        </p>
        <Divider />
        {error && <Message negative content={error} />}
        <Form>
          <Form.Group>
            <Form.Input
              width={formEnteredFromResult ? 16 : 12}
              label="Property Address"
              placeholder="123 Main St, Denver, CO 80202"
              value={inputs.address}
              onChange={(e, { value }) =>
                formEnteredFromResult ? handleAddressFieldAttemptedChange() : updateField("address", value)
              }
            />
            {/* Research This Property is hidden while regenerating an already-
                unlocked property (formEnteredFromResult) - re-researching here
                would imply a different property, which the confirmation
                prompt above handles explicitly instead. */}
            {!formEnteredFromResult && (
              <Form.Field width={4} style={{ display: "flex", alignItems: "flex-end" }}>
                <Button
                  type="button"
                  fluid
                  icon
                  labelPosition="left"
                  disabled={!inputs.address}
                  onClick={handleResearch}
                >
                  <Icon name="search" />
                  Research This Property
                </Button>
              </Form.Field>
            )}
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              label={isBuyer ? "Listing Price" : "Current / Planned List Price"}
              placeholder="$450,000"
              value={inputs.currentPrice}
              onChange={(e, { value }) => updateField("currentPrice", value)}
            />
            <Form.Select
              label="Condition of the Home"
              placeholder="Select condition"
              options={conditionOptions}
              value={inputs.condition}
              onChange={(e, { value }) => updateField("condition", value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              width={5}
              label="Property Type"
              placeholder="Single family, condo, townhome..."
              value={inputs.propertyType}
              onChange={(e, { value }) => updateField("propertyType", value)}
            />
            <Form.Input
              width={2}
              label="Bedrooms"
              placeholder="3"
              value={inputs.bedrooms}
              onChange={(e, { value }) => updateField("bedrooms", value)}
            />
            <Form.Input
              width={2}
              label="Bathrooms"
              placeholder="2.5"
              value={inputs.bathrooms}
              onChange={(e, { value }) => updateField("bathrooms", value)}
            />
            <Form.Input
              width={2}
              label="Square Feet"
              placeholder="2,200"
              value={inputs.squareFeet}
              onChange={(e, { value }) => updateField("squareFeet", value)}
            />
            <Form.Input
              width={3}
              label="Lot Size (sqft/acres)"
              placeholder="0.25 acres"
              value={inputs.lotSize}
              onChange={(e, { value }) => updateField("lotSize", value)}
            />
            <Form.Input
              width={2}
              label="Year Built"
              placeholder="1998"
              value={inputs.yearBuilt}
              onChange={(e, { value }) => updateField("yearBuilt", value)}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Input
              label="Time on Market"
              placeholder="45 days"
              value={inputs.timeOnMarket}
              onChange={(e, { value }) => updateField("timeOnMarket", value)}
            />
            <Form.Input
              label="Tax Assessed Value"
              placeholder="$410,000"
              value={inputs.taxAssessedValue}
              onChange={(e, { value }) => updateField("taxAssessedValue", value)}
            />
          </Form.Group>
          {isBuyer && (
            <>
              <Form.Group widths="equal">
                <Form.Input
                  label="Original List Date"
                  placeholder="e.g. 3/15/2025"
                  value={inputs.originalListDate}
                  onChange={(e, { value }) => updateField("originalListDate", value)}
                />
                <Form.Input
                  label="Original List Price"
                  placeholder="$475,000"
                  value={inputs.originalListPrice}
                  onChange={(e, { value }) => updateField("originalListPrice", value)}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="What the Seller Paid"
                  placeholder="$390,000"
                  value={inputs.sellerPurchasePrice}
                  onChange={(e, { value }) => updateField("sellerPurchasePrice", value)}
                />
                <Form.Input
                  label="Date Seller Purchased"
                  placeholder="e.g. 6/2019"
                  value={inputs.sellerPurchaseDate}
                  onChange={(e, { value }) => updateField("sellerPurchaseDate", value)}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Select
                  label="Home Status"
                  placeholder="Select home status"
                  options={HOME_STATUS_OPTIONS}
                  value={inputs.homeStatus}
                  onChange={(e, { value }) => updateField("homeStatus", value)}
                />
                <Form.Field />
              </Form.Group>
              <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "-8px" }}>
                Purchase price/date are filled in automatically from public record when found accurately;
                home status is not auto-detected - enter what you know and it will be used to refine the
                offer strategy and negotiation context, never to determine market value.
              </p>
            </>
          )}
          <Form.TextArea
            label="Key Features"
            placeholder="Granite counters, 3-car garage, mountain views..."
            value={inputs.keyFeatures}
            onChange={(e, { value }) => updateField("keyFeatures", value)}
          />

          <Form.TextArea
            label="Recent Updates / Renovations"
            placeholder="New roof (2024), remodeled kitchen, new HVAC..."
            value={inputs.updatesRenovations}
            onChange={(e, { value }) => updateField("updatesRenovations", value)}
          />
          <Form.TextArea
            label="Known Issues / Needed Repairs"
            placeholder="Aging water heater, cosmetic repairs needed in..."
            value={inputs.knownIssues}
            onChange={(e, { value }) => updateField("knownIssues", value)}
          />
          <Form.Group widths="equal">
            <Form.TextArea
              label="Location Pros"
              placeholder="Cul-de-sac, top-rated school district, walkable..."
              value={inputs.locationPros}
              onChange={(e, { value }) => updateField("locationPros", value)}
            />
            <Form.TextArea
              label="Location Cons"
              placeholder="Busy street, near power lines, HOA restrictions..."
              value={inputs.locationCons}
              onChange={(e, { value }) => updateField("locationCons", value)}
            />
          </Form.Group>
          <Form.TextArea
            label="Comparable Sales / Listings You're Aware Of"
            placeholder="123 Main St sold for $460k in June, similar sq ft..."
            value={inputs.comparableSales}
            onChange={(e, { value }) => updateField("comparableSales", value)}
          />

          <Form.TextArea
            label="Competing Inventory Nearby"
            placeholder="2 other similar homes currently listed nearby..."
            value={inputs.competingInventory}
            onChange={(e, { value }) => updateField("competingInventory", value)}
          />

          {isBuyer && (
            <>
              <Divider horizontal>Buyer Details</Divider>
              <Form.Group widths="equal">
                <Form.Input
                  label="Buyer's Max Budget"
                  placeholder="$475,000"
                  value={inputs.budget}
                  onChange={(e, { value }) => updateField("budget", value)}
                />
                <Form.Select
                  label="Financing Type"
                  placeholder="Select financing"
                  options={financingOptions}
                  value={inputs.financingType}
                  onChange={(e, { value }) => updateField("financingType", value)}
                />
              </Form.Group>
              <Form.TextArea
                label="Multiple Offer Situation / Competition"
                placeholder="Sellers mentioned 2 other offers expected..."
                value={inputs.multipleOffers}
                onChange={(e, { value }) => updateField("multipleOffers", value)}
              />
            </>
          )}

          <Form.Select
            label="Timeline / Urgency"
            placeholder="Select timeline"
            options={timelineOptions}
            value={inputs.timeline}
            onChange={(e, { value }) => updateField("timeline", value)}
          />
          <Form.TextArea
            label="Additional Notes"
            placeholder="Anything else that would help explain the price..."
            value={inputs.additionalNotes}
            onChange={(e, { value }) => updateField("additionalNotes", value)}
          />
        </Form>
      </>
    );
  }

  function renderResultStep() {
    const dedupedSaleHistory = dedupeSaleHistory(subjectLookup?.saleHistory);
    const fullAddress = convertAddressFull(transaction?.address);

    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Header color="blue" size="large" style={{ marginBottom: "4px" }}>
              <Icon name="dollar" />
              {isBuyer ? "Offer Pricing Strategy" : "List Pricing Strategy"}
            </Header>
            {fullAddress && (
              <div className="text-grey" style={{ fontSize: "1em", marginBottom: "4px" }}>
                {fullAddress}
              </div>
            )}
            {toDisplayDate(result?.generatedAt) && (
              <div className="text-grey" style={{ fontSize: "0.85em", marginBottom: "4px" }}>
                Generated {toDisplayDate(result.generatedAt)}
              </div>
            )}
            {(inputs.currentPrice || inputs.timeOnMarket) && (
              <div style={{ fontSize: "0.92em", marginBottom: "4px" }}>
                {inputs.currentPrice && (
                  <span>
                    <span className="text-grey">Current List Price:</span> <strong>{inputs.currentPrice}</strong>
                  </span>
                )}
                {inputs.currentPrice && inputs.timeOnMarket && <span className="text-grey"> &nbsp;&middot;&nbsp; </span>}
                {inputs.timeOnMarket && (
                  <span>
                    <span className="text-grey">Approx. Days on Market:</span> <strong>{inputs.timeOnMarket}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
          <Button
            basic
            icon
            labelPosition="left"
            size="small"
            className="no-print"
            onClick={() => window.print()}
          >
            <Icon name="print" />
            Print
          </Button>
        </div>
        <Divider />

        {result?.valuationMode === "pre_listing" && (
          <Segment secondary style={{ marginBottom: "12px" }}>
            <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em", marginBottom: "6px" }}>
              PROPERTY HISTORY
            </div>
            {dedupedSaleHistory.length > 0 ? (
              <>
                <Grid stackable columns={result?.impliedChangeSincePriorSale ? 3 : 2}>
                  <Grid.Column>
                    <div className="text-grey" style={{ fontSize: "0.75em" }}>LAST SOLD</div>
                    <div style={{ fontSize: "1.2em", fontWeight: 700 }}>
                      ${Number(dedupedSaleHistory[0].price).toLocaleString()}
                    </div>
                    <div className="text-grey" style={{ fontSize: "0.85em" }}>
                      {formatSoldDate(dedupedSaleHistory[0].date)}
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <div className="text-grey" style={{ fontSize: "0.75em" }}>SOURCE</div>
                    <div>{dedupedSaleHistory[0].source}</div>
                  </Grid.Column>
                  {result?.impliedChangeSincePriorSale && (
                    <Grid.Column>
                      <div className="text-grey" style={{ fontSize: "0.75em" }}>
                        IMPLIED CHANGE SINCE PRIOR SALE
                      </div>
                      <div style={{
                        fontWeight: 600,
                        color: result.impliedChangeSincePriorSale.absoluteChange >= 0 ? "#21ba45" : "#db2828",
                      }}>
                        {result.impliedChangeSincePriorSale.absoluteChange >= 0 ? "+" : ""}
                        ${result.impliedChangeSincePriorSale.absoluteChange.toLocaleString()}
                        {" "}({result.impliedChangeSincePriorSale.percentChange >= 0 ? "+" : ""}
                        {result.impliedChangeSincePriorSale.percentChange}%)
                      </div>
                    </Grid.Column>
                  )}
                </Grid>
                {(subjectLookup?.publicRecord?.lastSale?.buyerNames?.length > 0 ||
                  subjectLookup?.publicRecord?.lastSale?.sellerNames?.length > 0) && (
                  <Grid stackable columns={2} style={{ marginTop: "4px" }}>
                    {subjectLookup.publicRecord.lastSale.buyerNames.length > 0 && (
                      <Grid.Column>
                        <div className="text-grey" style={{ fontSize: "0.75em" }}>BUYER</div>
                        <div style={{ fontSize: "0.92em" }}>{subjectLookup.publicRecord.lastSale.buyerNames.join(", ")}</div>
                      </Grid.Column>
                    )}
                    {subjectLookup.publicRecord.lastSale.sellerNames.length > 0 && (
                      <Grid.Column>
                        <div className="text-grey" style={{ fontSize: "0.75em" }}>SELLER</div>
                        <div style={{ fontSize: "0.92em" }}>{subjectLookup.publicRecord.lastSale.sellerNames.join(", ")}</div>
                      </Grid.Column>
                    )}
                  </Grid>
                )}
                {dedupedSaleHistory.length > 1 && (
                  <Popup
                    trigger={
                      <span
                        style={{
                          display: "inline-block",
                          position: "relative",
                          zIndex: 1,
                          padding: "4px 0",
                          fontSize: "0.9em",
                          cursor: "pointer",
                          color: "#2185d0",
                        }}
                      >
                        View full sale history ({dedupedSaleHistory.length} sales)
                      </span>
                    }
                    content={
                      <List>
                        {dedupedSaleHistory.map((s, i) => (
                          <List.Item key={i}>
                            ${Number(s.price).toLocaleString()} on {formatSoldDate(s.date)}
                          </List.Item>
                        ))}
                      </List>
                    }
                  />
                )}
                <p className="text-grey" style={{ fontSize: "0.82em", marginTop: "8px", marginBottom: 0 }}>
                  Prior sale history is supporting context and is not used as a substitute for current comparable sales.
                </p>

                {subjectLookup?.publicRecord?.latestRecordedFinancing && (
                  <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    <Grid stackable columns={2}>
                      <Grid.Column>
                        <div className="text-grey" style={{ fontSize: "0.75em" }}>LATEST RECORDED FINANCING</div>
                        <div style={{ fontSize: "1.05em", fontWeight: 600 }}>
                          {Number.isFinite(subjectLookup.publicRecord.latestRecordedFinancing.originalLoanAmount)
                            ? `$${subjectLookup.publicRecord.latestRecordedFinancing.originalLoanAmount.toLocaleString()}`
                            : "Amount not recorded"}
                        </div>
                        <div className="text-grey" style={{ fontSize: "0.85em" }}>
                          {formatSoldDate(subjectLookup.publicRecord.latestRecordedFinancing.recordedDate)}
                          {subjectLookup.publicRecord.latestRecordedFinancing.lender
                            ? ` · ${subjectLookup.publicRecord.latestRecordedFinancing.lender}`
                            : ""}
                          {subjectLookup.publicRecord.latestRecordedFinancing.loanType
                            ? ` · ${subjectLookup.publicRecord.latestRecordedFinancing.loanType}`
                            : ""}
                        </div>
                        {subjectLookup.publicRecord.latestRecordedFinancing.possibleRefinance && (
                          <Label size="mini" basic style={{ marginTop: "4px" }}>Possible refinance</Label>
                        )}
                      </Grid.Column>
                      {subjectLookup.publicRecord.loanHistory?.length > 1 && (
                        <Grid.Column verticalAlign="bottom">
                          <Popup
                            trigger={
                              <span style={{ fontSize: "0.85em", cursor: "pointer", color: "#2185d0" }}>
                                View financing history ({subjectLookup.publicRecord.loanHistory.length} records)
                              </span>
                            }
                            content={
                              <List>
                                {subjectLookup.publicRecord.loanHistory.map((f, i) => (
                                  <List.Item key={i}>
                                    {Number.isFinite(f.originalLoanAmount) ? `$${f.originalLoanAmount.toLocaleString()}` : "Amount not recorded"}
                                    {" on "}{formatSoldDate(f.recordedDate)}
                                    {f.lender ? ` · ${f.lender}` : ""}
                                    {f.possibleRefinance ? " (possible refinance)" : ""}
                                  </List.Item>
                                ))}
                              </List>
                            }
                          />
                        </Grid.Column>
                      )}
                    </Grid>
                    <p className="text-grey" style={{ fontSize: "0.78em", marginTop: "6px", marginBottom: 0 }}>
                      Recorded loan detail from public record - not the seller's current mortgage balance, and not used
                      as evidence of market value.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-grey" style={{ marginBottom: 0 }}>No verified prior sale identified.</p>
            )}
          </Segment>
        )}

        <ComparableReviewPanel
          comps={comps}
          excludedIndices={excludedCompIndices}
          onToggleComp={toggleCompIncluded}
          reviewCandidates={reviewCandidates}
          reviewInclusions={reviewInclusions}
          onIncludeReviewCandidate={includeReviewCandidate}
          onRemoveReviewInclusion={removeReviewInclusion}
          compSetStrength={result?.compSetStrength || compSearchSummary?.compSetStrength}
          stale={resultIsStale}
          onRegenerate={handleGenerate}
          regenerating={step === "loading"}
        />

        {/* Never silently shown as current when the comp set changed. */}
        <div style={resultIsStale ? { opacity: 0.55 } : undefined} aria-disabled={resultIsStale || undefined}>
          {resultIsStale && <Label color="orange" ribbon>Outdated - regenerate to update</Label>}
          <PricingStrategyResultBody result={result} isBuyer={isBuyer} compSearchSummary={compSearchSummary} canRegenerate />
        </div>


        {/* Explain to client - interactive-only tool (compose/copy/email), never printed */}
        {result?.clientExplanation && resultIsStale && (
          <Message warning size="small" className="no-print" content="The client explanation is unavailable until the strategy is regenerated with the current comparable sales." />
        )}
        {result?.clientExplanation && !resultIsStale && (
          <Segment className="no-print" style={{ marginTop: "12px" }}>
            <Button
              basic
              icon
              labelPosition="left"
              size="small"
              className="no-print"
              onClick={() => setShowClientExplanation((v) => !v)}
            >
              <Icon name="comment outline" />
              {isBuyer ? "Explain to Buyer" : "Explain to Seller"}
            </Button>
            {showClientExplanation && (
              <div style={{ marginTop: "12px" }}>
                <p style={{ whiteSpace: "pre-wrap" }}>{result.clientExplanation}</p>
                <Button size="small" icon labelPosition="left" className="no-print" onClick={handleCopyClientExplanation}>
                  <Icon name="copy outline" />
                  Copy to Clipboard
                </Button>
                <Button
                  size="small"
                  icon
                  labelPosition="left"
                  className="no-print"
                  onClick={() => {
                    setShowEmailCompose((v) => !v);
                    setCheckboxNotice(null);
                  }}
                >
                  <Icon name="mail" />
                  {isBuyer ? "Create Email to Buyers" : "Create Email to Sellers"}
                </Button>

                {showEmailCompose && (
                  <Segment secondary className="no-print" style={{ marginTop: "12px" }}>
                    <Checkbox
                      label="Include Pricing Approaches"
                      checked={includeApproachesInEmail}
                      onChange={(e, { checked }) => handleToggleEmailSection("approaches", checked)}
                      disabled={!result?.pricingApproaches?.length}
                      style={{ display: "block", marginBottom: "8px" }}
                    />
                    <Checkbox
                      label="Include Comparable Sales"
                      checked={includeCompsInEmail}
                      onChange={(e, { checked }) => handleToggleEmailSection("comps", checked)}
                      disabled={!result?.comparableSales?.length}
                      style={{ display: "block", marginBottom: "12px" }}
                    />

                    {checkboxNotice && (
                      <Message warning size="small" onDismiss={() => setCheckboxNotice(null)}>
                        <Message.Header>You've already started editing this email</Message.Header>
                        <p style={{ marginTop: "4px" }}>
                          {checkboxNotice.checked
                            ? `Turning on "${checkboxNotice.label}" won't automatically add it to your edited text.`
                            : `Turning off "${checkboxNotice.label}" won't automatically remove it if it's already in your edited text.`}
                        </p>
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                          <Button size="mini" onClick={handleResetEmailBody}>
                            Reset to Suggested Text
                          </Button>
                          {checkboxNotice.checked && checkboxNotice.snippet && (
                            <Button size="mini" basic onClick={() => handleCopySnippet(checkboxNotice.snippet)}>
                              Copy "{checkboxNotice.label}" Text
                            </Button>
                          )}
                        </div>
                      </Message>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <div className="text-grey" style={{ fontSize: "0.8em", fontWeight: 600, marginBottom: "4px" }}>
                        EMAIL BODY (EDITABLE)
                      </div>
                      {emailBodyEdited && (
                        <span
                          style={{ fontSize: "0.8em", cursor: "pointer", color: "#2185d0" }}
                          onClick={handleResetEmailBody}
                        >
                          Reset to suggested text
                        </span>
                      )}
                    </div>
                    <textarea
                      value={emailBodyDraft}
                      onChange={(e) => {
                        setEmailBodyDraft(e.target.value);
                        setEmailBodyEdited(true);
                      }}
                      rows={10}
                      style={{
                        width: "100%",
                        fontFamily: "inherit",
                        fontSize: "0.9em",
                        padding: "8px",
                        border: "1px solid rgba(34,36,38,0.15)",
                        borderRadius: "4px",
                        resize: "vertical",
                      }}
                    />

                    {getClientRecipients().length === 0 ? (
                      <Message negative size="small" style={{ marginTop: "10px" }}>
                        No email address is on file for the {isBuyer ? "buyer(s)" : "seller(s)"} on this transaction.
                      </Message>
                    ) : (
                      <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "10px" }}>
                        To: {getClientRecipients()
                          .map((r) => (r.name ? `${r.name} <${r.email}>` : r.email))
                          .join(", ")}
                      </p>
                    )}

                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      loading={sendingEmail}
                      disabled={sendingEmail || getClientRecipients().length === 0}
                      onClick={handleSendClientEmail}
                    >
                      <Icon name="send" />
                      Send Email
                    </Button>
                  </Segment>
                )}
              </div>
            )}
          </Segment>
        )}

        <Message info size="small" style={{ marginTop: "16px" }}>
          <Icon name="info circle" />
          {buildDynamicDisclaimer(result?.sourcesUsed)}
        </Message>
      </>
    );
  }

  // Print letterhead (spec: agent's logo, full name, brokerage, phone, and
  // email as a heading at the top of the first printed page) - hidden on
  // screen via .print-only, shown only inside the print stylesheet. Prefers
  // the transaction's agent-of-record over whoever happens to be viewing
  // (e.g. an assistant), matching the fallback order already used for the
  // brokerage logo in handleSendClientEmail above.
  const letterheadAgentName = `${
    transaction?.agentProfile?.firstName || currentUserProfile?.firstName || ""
  } ${transaction?.agentProfile?.lastName || currentUserProfile?.lastName || ""}`.trim();
  const letterheadBrokerageName = transaction?.agentProfile?.brokerageName || currentUserProfile?.brokerageName || "";
  const letterheadPhone = transaction?.agentProfile?.phone || currentUserProfile?.phone || "";
  const letterheadEmail = transaction?.agentProfile?.email || currentUserProfile?.email || "";
  const letterheadLogoUrl = transaction?.agentProfile?.brokerageLogoRef || currentUserProfile?.brokerageLogoRef || null;

  return (
    <>
    <ModalWrapper
      size={step === "result" && feedbackExpanded ? "fullscreen" : "large"}
      className={step === "result" && !feedbackExpanded ? "pricing-strategy-letter-width" : undefined}
    >
      <Segment clearing id="pricing-strategy-print-area">
        <div
          className="print-only"
          style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: "2px solid #2185d0" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {letterheadLogoUrl && (
              <img src={letterheadLogoUrl} alt="Brokerage logo" style={{ maxHeight: "60px", maxWidth: "160px" }} />
            )}
            <div>
              {letterheadAgentName && <div style={{ fontSize: "1.1em", fontWeight: 700 }}>{letterheadAgentName}</div>}
              {letterheadBrokerageName && <div style={{ fontSize: "0.95em" }}>{letterheadBrokerageName}</div>}
              {(letterheadPhone || letterheadEmail) && (
                <div style={{ fontSize: "0.85em", color: "#555" }}>
                  {letterheadPhone}
                  {letterheadPhone && letterheadEmail ? " · " : ""}
                  {letterheadEmail}
                </div>
              )}
            </div>
          </div>
        </div>
        {step === "loading" &&
          renderLoadingStep({ loadingText: generatingPhaseText })}
        {step === "researching" &&
          renderLoadingStep({
            explanation:
              "The AI is searching the web for this property's asking price, time on market, tax assessed value, square footage, lot size, key features, and recent sold comps in the neighborhood.",
            note:
              "Once it's done, you'll be able to review what it found and fill in anything you know about the home yourself — like its condition, recent updates, or known issues — which can change the pricing strategy.",
            loadingText: "Researching this property online...",
          })}
        {step === "rundown" && renderRundownStep()}
        {step === "form" && renderFormStep()}
        {step === "result" && (
          <Grid stackable>
            <Grid.Column mobile={16} tablet={16} computer={feedbackExpanded && !isMobile ? 11 : 16}>
              {renderResultStep()}
            </Grid.Column>
            {!isMobile && (
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={5}
                className="no-print"
                style={{ display: feedbackExpanded ? "block" : "none" }}
              >
                {result?.propertyKey && (
                  <StrategyFeedback
                    transactionId={transaction.id}
                    propertyKey={result.propertyKey}
                    propertyAddress={transaction?.address}
                    strategyType="pricing"
                    generationId={result.generationId}
                    generatedRecommendationSnapshot={{
                      recommendedListPrice: result.recommendedListPrice,
                      estimatedValueLow: result.estimatedValueLow,
                      estimatedValueHigh: result.estimatedValueHigh,
                      confidence: result.confidence,
                    }}
                    engineMetadata={{ modelName: result.modelName || null }}
                    config={buildPricingFeedbackConfig(isBuyer)}
                    expanded={feedbackExpanded}
                    onToggleExpanded={setFeedbackExpanded}
                    onStatusChange={setFeedbackStatus}
                    hideToggle
                  />
                )}
              </Grid.Column>
            )}
          </Grid>
        )}

        {step !== "loading" && step !== "researching" && (
          <>
            <Divider className="medium top margin" />
            {step === "rundown" && (
              <>
                <Popup
                  trigger={
                    <span style={{ float: "right" }}>
                      <Button
                        primary
                        icon
                        labelPosition="left"
                        disabled={!inputs.address}
                        onClick={handleGenerate}
                        data-test="pricing-strategy-submit"
                      >
                        <Icon name="magic" />
                        Generate Strategy
                      </Button>
                    </span>
                  }
                  content={
                    inputs.address
                      ? "Generates a pricing strategy using the listing details shown above, as-is."
                      : 'Add an address in "Edit Details About the Home" or the transaction overview before generating a strategy.'
                  }
                  position="top center"
                  size="small"
                />
                <Button
                  floated="right"
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setFormEnteredFromResult(false);
                    setStep("form");
                  }}
                >
                  <Icon name="edit" />
                  Edit Details About the Home
                </Button>
              </>
            )}
            {step === "result" && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                {!isMobile && !feedbackExpanded && !feedbackStatus.hidden ? (
                  <Popup
                    disabled={!feedbackStatus.alreadyDone}
                    content="Feedback is already complete for this Strategy on this property."
                    trigger={
                      <span>
                        <Button
                          basic
                          icon
                          labelPosition="left"
                          className="no-print"
                          disabled={feedbackStatus.alreadyDone}
                          onClick={() => !feedbackStatus.alreadyDone && setFeedbackExpanded(true)}
                          data-test="strategy-feedback-toggle"
                        >
                          <Icon name="comment outline" />
                          {feedbackStatus.buttonLabel}
                        </Button>
                      </span>
                    }
                  />
                ) : (
                  <span />
                )}
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    primary
                    icon
                    labelPosition="left"
                    className="no-print"
                    onClick={() => {
                      setFormEnteredFromResult(true);
                      setStep("form");
                    }}
                  >
                    <Icon name="redo" />
                    Edit Details &amp; Regenerate
                  </Button>
                  <Button className="no-print" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            )}
            {step === "form" && (
              <Popup
                trigger={
                  <span style={{ float: "right" }}>
                    <Button
                      primary
                      icon
                      labelPosition="left"
                      disabled={!inputs.address}
                      onClick={handleGenerate}
                      data-test="pricing-strategy-submit"
                    >
                      <Icon name="magic" />
                      Generate Strategy
                    </Button>
                  </span>
                }
                content={
                  inputs.address
                    ? "Generates a pricing strategy using the details entered above."
                    : 'Add an address in "Edit Details About the Home" or the transaction overview before generating a strategy.'
                }
                position="top center"
                size="small"
              />
            )}
            {step !== "result" && (
              <Button floated="left" className="no-print" onClick={handleClose}>
                Close
              </Button>
            )}
          </>
        )}
      </Segment>
    </ModalWrapper>

    {showDifferentPropertyPrompt && (
      <Modal size="small" open onClose={handleCancelDifferentPropertyPrompt} closeIcon>
        <Modal.Header>Different property?</Modal.Header>
        <Modal.Content>
          <p>
            Are you looking for a pricing strategy on a different property? If so, a new strategy
            can be generated and will cost an additional credit.
          </p>
          <p>
            One Strategy Credit works on one address. All Strategies are saved in this transaction
            for you to reference.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelDifferentPropertyPrompt}>Cancel</Button>
          <Button primary onClick={handleConfirmDifferentProperty}>
            Yes, different property
          </Button>
        </Modal.Actions>
      </Modal>
    )}

    {showNewAddressForm && (
      <Modal size="small" open onClose={handleCancelNewAddressForm} closeIcon>
        <Modal.Header>New Property Address</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group>
              <Form.Input
                width={12}
                label="Street address"
                value={newPropertyAddress.street}
                onChange={(e, { value }) => updateNewPropertyAddressField("street", value)}
              />
              <Form.Input
                width={4}
                label="Unit #"
                value={newPropertyAddress.unit}
                onChange={(e, { value }) => updateNewPropertyAddressField("unit", value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Input
                width={8}
                label="City"
                value={newPropertyAddress.city}
                onChange={(e, { value }) => updateNewPropertyAddressField("city", value)}
              />
              <Form.Input
                width={4}
                label="State"
                value={newPropertyAddress.state}
                onChange={(e, { value }) => updateNewPropertyAddressField("state", value)}
              />
              <Form.Input
                width={4}
                label="Zipcode"
                value={newPropertyAddress.zipcode}
                onChange={(e, { value }) => updateNewPropertyAddressField("zipcode", value)}
              />
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={switchingProperty} onClick={handleCancelNewAddressForm}>
            Cancel
          </Button>
          <Button
            primary
            loading={switchingProperty}
            disabled={switchingProperty || !newPropertyAddress.street || !newPropertyAddress.city || !newPropertyAddress.state}
            onClick={handleSwitchToNewProperty}
          >
            Continue to Unlock Strategies
          </Button>
        </Modal.Actions>
      </Modal>
    )}
    </>
  );
}
